import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';

const Home = () => {
  const { getContent } = useContent();
  const [activeGalleryTab, setActiveGalleryTab] = useState('photos');
  
  const heroContent = getContent('home.hero') || {};
  const featuredPrograms = getContent('home.featuredPrograms') || {};
  const featuresHighlight = getContent('home.featuresHighlight') || {};
  const expertTestimonials = getContent('home.expertTestimonials') || {};
  const participantTestimonials = getContent('home.participantTestimonials') || {};
  const upcomingSessions = getContent('home.upcomingSessions') || {};
  const expertGallery = getContent('home.expertGallery') || {};
  const ctaContent = getContent('home.cta') || {};

  // Static hero buttons - no more flashing!
  const heroButtons = (
    <>
      <Link to="/formations" className="btn btn-primary btn-hero-primary">
        <i className="fas fa-graduation-cap"></i>
        Découvrir nos Formations
      </Link>
      <Link to="/contact" className="btn btn-primary">
        <i className="fas fa-phone"></i>
        Nous Contacter
      </Link>
    </>
  );



  useEffect(() => {
    const video = document.querySelector('.hero-video');
    
    if (video) {
      const videoUrl = heroContent.videoUrl || '/video.mp4';
      
      // Update video source and force reload
      const source = video.querySelector('source');
      if (source && source.src !== videoUrl) {
        source.src = videoUrl;
        video.load(); // Force video to reload with new source
      }
      
      // Optimized play function
      const playVideo = async () => {
        try {
          video.muted = true;
          video.playsInline = true;
          await video.play();
        } catch (error) {
          // Retry on first user interaction
          const handleInteraction = () => {
            video.play().catch(() => {});
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
          };
          document.addEventListener('touchstart', handleInteraction, { passive: true, once: true });
          document.addEventListener('click', handleInteraction, { once: true });
        }
      };

      // Single optimized event listener
      const handleReady = () => {
        playVideo();
      };

      // Use only the most effective event
      video.addEventListener('loadeddata', handleReady, { once: true });
      
      // Lazy load video for better initial performance
      setTimeout(() => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'video';
        link.href = videoUrl;
        link.type = 'video/mp4';
        document.head.appendChild(link);
      }, 1000);
      
      // Immediate attempt
      if (video.readyState >= 3) {
        playVideo();
      }

      // Cleanup
      return () => {
        video.removeEventListener('loadeddata', handleReady);
      };
    }
  }, [heroContent.videoUrl]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero" id="hero">
        <video 
          key={heroContent.videoUrl || '/video.mp4'}
          className="hero-video" 
          autoPlay 
          muted 
          playsInline 
          preload="metadata"
          loop
          poster=""
          webkit-playsinline="true"
          x-webkit-airplay="deny"
        >
          <source src={heroContent.videoUrl || '/video.mp4'} type="video/mp4" />
        </video>
        <div className="hero-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title" dangerouslySetInnerHTML={{ __html: heroContent.title || 'Dévoué à faire évoluer votre carrière' }}>
            </h1>
            <p className="hero-subtitle">
              {heroContent.subtitle || 'Leader de la formation dentaire en Algérie. Spécialisé en technologie Cone Beam CBCT, chirurgie orale avancée, prothèse, implantologie et orthodontie moderne.'}
            </p>
            <div className="hero-stats">
              {(heroContent.stats || []).map((stat, index) => (
                <div key={index} className="stat">
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
            <div className="hero-buttons">
              {heroButtons}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="featured-programs" id="featured-programs">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{featuredPrograms.title || 'Nos Formations Phares'}</h2>
            <p className="section-subtitle">
              {featuredPrograms.subtitle || 'Des programmes de formation avancés conçus pour élever votre pratique dentaire'}
            </p>
          </div>
          <div className="programs-grid">
            {(featuredPrograms.programs || []).map((program, index) => (
              <div key={index} className={`program-card ${index === 0 ? 'featured' : ''}`}>
                <div className="program-image">
                  {program.image ? (
                    <img src={program.image} alt={program.title} />
                  ) : (
                    <div className="program-placeholder">
                      <i className={program.icon || 'fas fa-graduation-cap'}></i>
                    </div>
                  )}
                </div>
                <div className="program-content">
                  <h3>{program.title}</h3>
                  <p>{program.description}</p>
                  <div className="program-features">
                    {(program.features || []).map((feature, featureIndex) => (
                      <span key={featureIndex} className="feature">{feature}</span>
                    ))}
                  </div>
                  <Link to={program.link} className="btn btn-outline">
                    <i className="fas fa-arrow-right"></i>
                    En savoir plus
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="features-highlight" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{featuresHighlight.title || 'Pourquoi Choisir DentoXcellence ?'}</h2>
            <p className="section-subtitle">
              {featuresHighlight.subtitle || 'L\'excellence reconnue en formation dentaire avec des résultats exceptionnels'}
            </p>
          </div>
          <div className="features-showcase">
            {(featuresHighlight.features || []).map((feature, index) => (
              <div key={index} className={`feature-card ${index === 0 ? 'premium' : index === 1 ? 'excellence' : index === 2 ? 'expertise' : 'satisfaction'}`}>
                <div className="feature-photo">
                  {feature.photo ? (
                    <img src={feature.photo} alt={feature.title} />
                  ) : (
                    <div className="feature-placeholder">
                      <i className={feature.icon || 'fas fa-star'}></i>
                    </div>
                  )}
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <div className="feature-stats">
                    <span className="stat-highlight">{feature.stat}</span>
                    <span className="stat-desc">{feature.statDesc}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
            </div>
      </section>

      {/* Témoignages de nos Experts */}
      <section className="expert-testimonials" id="expert-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{expertTestimonials.title || 'Témoignages de nos Experts'}</h2>
            <p className="section-subtitle">
              {expertTestimonials.subtitle || 'Découvrez l\'expertise et l\'expérience de nos formateurs de renommée internationale'}
            </p>
          </div>
          <div className="testimonials-grid">
            {(expertTestimonials.testimonials || []).map((expert, index) => (
              <div key={expert.id} className="testimonial-card expert-testimonial">
                <div className="testimonial-header">
                  <div className="expert-photo">
                    {expert.photo ? (
                      <img src={expert.photo} alt={expert.name} />
                    ) : (
                <i className="fas fa-user-md"></i>
                    )}
                  </div>
                  <div className="expert-info">
                    <h4>{expert.name}</h4>
                    <span className="expert-title">{expert.title}</span>
                    <span className="expert-formation">Formation: {expert.formation}</span>
                  </div>
                  {expert.type === 'video' && expert.youtube_id && (
                    <div className="video-indicator">
                      <i className="fab fa-youtube"></i>
                      <span>Témoignage Vidéo</span>
              </div>
                  )}
                </div>
                <div className="testimonial-content">
                  {expert.type === 'video' && expert.youtube_id ? (
                    <div className="video-testimonial">
                      <div 
                        className="youtube-video-container"
                        onClick={() => {
                          window.open(expert.youtube_url, '_blank');
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          marginBottom: '1rem'
                        }}
                      >
                        <img 
                          src={expert.video_thumbnail} 
                          alt={`Témoignage vidéo de ${expert.name}`}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#ff0000',
                          fontSize: '3rem',
                          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                          transition: 'transform 0.2s ease'
                        }}>
                          <i className="fab fa-youtube"></i>
                </div>
              </div>
            </div>
                  ) : (
                    <div className="testimonial-text">
                      <p>"{expert.text}"</p>
                    </div>
                  )}
                  <div className="expert-specialties">
                    {expert.specialties.map((specialty, specialtyIndex) => (
                      <span key={specialtyIndex} className="specialty-badge">{specialty}</span>
                    ))}
              </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages de nos Participants */}
      <section className="participant-testimonials" id="participant-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{participantTestimonials.title || 'Témoignages de nos Participants'}</h2>
            <p className="section-subtitle">
              {participantTestimonials.subtitle || 'Découvrez comment nos formations ont transformé la carrière de dentistes à travers l\'Algérie'}
            </p>
          </div>
          <div className="testimonials-grid">
            {(participantTestimonials.testimonials || []).map((participant, index) => (
              <div key={participant.id} className="testimonial-card participant-testimonial">
                <div className="testimonial-header">
                  <div className="participant-photo">
                    {participant.photo ? (
                      <img src={participant.photo} alt={participant.name} />
                    ) : (
                      <i className="fas fa-user-doctor"></i>
                    )}
                  </div>
                  <div className="participant-info">
                    <h4>{participant.name}</h4>
                    <span className="participant-title">{participant.title}</span>
                    <span className="participant-formation">À propos: {participant.formation}</span>
                    <span className="participant-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {participant.location} • {participant.date}
                    </span>
                  </div>
                  {participant.type === 'video' && participant.youtube_id && (
                    <div className="video-indicator">
                      <i className="fab fa-youtube"></i>
                      <span>Témoignage Vidéo</span>
                    </div>
                  )}
                </div>
              <div className="testimonial-content">
                  {participant.type === 'video' && participant.youtube_id ? (
                    <div className="video-testimonial">
                      <div 
                        className="youtube-video-container"
                        onClick={() => {
                          window.open(participant.youtube_url, '_blank');
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          marginBottom: '1rem'
                        }}
                      >
                        <img 
                          src={participant.video_thumbnail} 
                          alt={`Témoignage vidéo de ${participant.name}`}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#ff0000',
                          fontSize: '3rem',
                          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                          transition: 'transform 0.2s ease'
                        }}>
                          <i className="fab fa-youtube"></i>
                  </div>
                </div>
              </div>
                  ) : (
                <div className="testimonial-text">
                      <p>"{participant.text}"</p>
                </div>
                  )}
                  <div className="testimonial-rating">
                    {[...Array(participant.rating || 5)].map((_, starIndex) => (
                      <i key={starIndex} className="fas fa-star"></i>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Training Sessions */}
      <section className="upcoming-sessions" id="upcoming-sessions">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{upcomingSessions.title || 'Prochaines Sessions de Formation'}</h2>
            <p className="section-subtitle">
              {upcomingSessions.subtitle || 'Inscrivez-vous dès maintenant aux formations à venir dans votre région'}
            </p>
          </div>
          <div className="sessions-grid">
            {(upcomingSessions.sessions || []).map((session, index) => (
              <div key={index} className="session-card">
                <div className={`session-badge ${session.badge.toLowerCase()}`}>
                  <i className={session.badgeIcon}></i>
                  {session.badge}
              </div>
              <div className="session-header">
                  <h3>{session.title}</h3>
                  <span className="session-trainer">avec {session.trainer}</span>
              </div>
              <div className="session-details">
                <div className="session-date">
                  <i className="fas fa-calendar"></i>
                    <span>{session.date}</span>
                </div>
                <div className="session-location">
                  <i className="fas fa-map-marker-alt"></i>
                    <span>{session.location}</span>
                </div>
                <div className="session-duration">
                  <i className="fas fa-clock"></i>
                    <span>{session.duration}</span>
                </div>
              </div>
              <div className="session-action">
                  <Link to="/formations#registration-form" className="btn btn-primary">
                  <i className="fas fa-user-plus"></i>
                  S'inscrire
                </Link>
              </div>
            </div>
            ))}
          </div>
          <div className="sessions-cta">
            <p>{upcomingSessions.ctaText || 'Plus de sessions disponibles dans d\'autres villes'}</p>
            <Link to={upcomingSessions.ctaLink || '/formations'} className="btn btn-outline">
              <i className="fas fa-calendar-alt"></i>
              {upcomingSessions.ctaButton || 'Voir Toutes les Sessions'}
            </Link>
          </div>
              </div>
      </section>

      {/* Expert Gallery */}
      <section className="expert-gallery" id="expert-gallery">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{expertGallery.title || 'Galerie Photo/Vidéo'}</h2>
            <p className="section-subtitle">
              {expertGallery.subtitle || 'Découvrez nos experts en action lors des formations et conférences'}
            </p>
              </div>
          
          <div className="gallery-tabs" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '2rem' }}>
            {/* FORCE both tabs to show - no conditional rendering */}
            <button
              className={`gallery-tab ${activeGalleryTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveGalleryTab('photos')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: activeGalleryTab === 'photos' ? '#3b82f6' : '#f3f4f6',
                color: activeGalleryTab === 'photos' ? 'white' : '#6b7280',
                fontWeight: activeGalleryTab === 'photos' ? '600' : '400',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-images"></i>
              Photos
            </button>
            <button
              className={`gallery-tab ${activeGalleryTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveGalleryTab('videos')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '25px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: activeGalleryTab === 'videos' ? '#3b82f6' : '#f3f4f6',
                color: activeGalleryTab === 'videos' ? 'white' : '#6b7280',
                fontWeight: activeGalleryTab === 'videos' ? '600' : '400',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-video"></i>
              Vidéos
            </button>
                </div>

          <div className="gallery-wrapper">
            {/* Photos Tab Content */}
            {activeGalleryTab === 'photos' && (
              <div className="gallery-content photos-content">
                {(expertGallery.photos || []).map((photo, index) => (
                  <div key={index} className="gallery-item">
                    {photo.data ? (
                      <img src={photo.data} alt={photo.title} />
                    ) : (
                      <div 
                        className="gallery-placeholder"
                        onClick={() => window.open('/admin#home.expertGallery', '_blank')}
                        title="Cliquez pour ajouter une photo via l'admin"
                      >
                        <i className={photo.icon || 'fas fa-camera'}></i>
                </div>
                    )}
                    <div className="gallery-overlay">
                      <h4>{photo.title}</h4>
                      <button 
                        className="gallery-btn"
                        onClick={() => window.open('/admin#home.expertGallery', '_blank')}
                      >
                        <i className="fas fa-cog"></i>
                      </button>
                </div>
              </div>
                ))}
              </div>
            )}

            {/* Videos Tab Content */}
            {activeGalleryTab === 'videos' && (
              <div className="gallery-content videos-content">
                {(expertGallery.videos || []).length === 0 ? (
                  <div className="gallery-empty-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <i className="fab fa-youtube" style={{ fontSize: '4rem', color: '#ff0000', marginBottom: '1rem' }}></i>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Aucune vidéo pour le moment</h3>
                    <p style={{ margin: '0 0 1.5rem 0', maxWidth: '400px' }}>
                      Les vidéos YouTube ajoutées via l'admin apparaîtront ici.
                    </p>
                  </div>
                ) : (
                  (expertGallery.videos || []).map((video, index) => (
                    <div key={index} className="gallery-item video-item">
                      {video.youtube_id ? (
                        <div 
                          className="youtube-video-container"
                          onClick={(e) => {
                            const url = video.youtube_url || `https://www.youtube.com/watch?v=${video.youtube_id}`;
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(url, '_blank');
                          }}
                          style={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        >
                          <img 
                            src={video.thumbnail} 
                            alt={video.title}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover'
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#ff0000',
                            fontSize: '3rem',
                            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                            transition: 'transform 0.2s ease'
                          }}>
                            <i className="fab fa-youtube"></i>
                          </div>
                        </div>
                      ) : (
                        <div className="gallery-placeholder">
                          <i className={video.icon || 'fas fa-play-circle'}></i>
                        </div>
                      )}
                      <div className="gallery-overlay">
                        <h4>{video.title}</h4>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="gallery-actions">
            {(expertGallery.actions || []).map((action, index) => (
              <a
                key={index}
                href={action.link}
                className={`btn btn-${action.type}`}
                target={action.target || '_self'}
                rel={action.target === '_blank' ? 'noopener noreferrer' : undefined}
              >
                <i className={action.icon}></i>
                {action.text}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{ctaContent.title || 'Prêt à Faire Évoluer Votre Carrière ?'}</h2>
            <p>{ctaContent.subtitle || 'Rejoignez les centaines de dentistes qui ont transformé leur pratique avec DentoXcellence'}</p>
            <div className="cta-buttons">
              {(ctaContent.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a key={index}
                     href={button.link} 
                     className={`btn btn-${button.type}`}
                     target="_blank" 
                     rel="noopener noreferrer">
                  <i className={button.icon}></i>
                  {button.text}
                </a>
                ) : (
                  <Link key={index} to={button.link} className={`btn btn-${button.type}`}>
                    <i className={button.icon}></i>
                    {button.text}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

